<template>
  <div class="modal fade" id="modalCrearEvento" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content divisan-modal border-0">
        <div class="modal-header border-0 pb-0">
          <div>
            <h5 class="fw-bold text-cafe mb-0">Programar mantenimiento</h5>
            <small class="fecha-texto">
              <i class="bi bi-calendar3 me-1"></i>{{ fechaFormateada }}
            </small>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <form @submit.prevent="guardar">
          <div class="modal-body">
            <div class="row g-3">
              <!-- ── Máquina ── -->
              <div class="col-12">
                <label class="form-label label-divisan">
                  Máquina <span class="text-danger">*</span>
                </label>
                <div class="custom-select-wrapper" v-click-outside="cerrarDropMaquina">
                  <div
                    class="form-control divisan-input-modal custom-select-input select-display"
                    @click="dropMaquina = !dropMaquina"
                  >
                    <span v-if="form.maquina">
                      {{ maquinas.find((m) => m.id === form.maquina)?.nombre }}
                    </span>
                    <span v-else class="placeholder-text">Selecciona una máquina</span>
                  </div>
                  <i
                    class="bi custom-select-arrow"
                    :class="dropMaquina ? 'bi-chevron-up' : 'bi-chevron-down'"
                    @mousedown.prevent="dropMaquina = !dropMaquina"
                  ></i>
                  <div v-if="dropMaquina" class="custom-dropdown">
                    <div
                      v-for="m in maquinas"
                      :key="m.id"
                      class="custom-dropdown-item"
                      :class="{ selected: form.maquina === m.id }"
                      @mousedown.prevent="elegirMaquina(m.id)"
                    >
                      {{ m.nombre }}
                    </div>
                    <div v-if="!maquinas.length" class="custom-dropdown-item text-muted">
                      Sin máquinas disponibles
                    </div>
                  </div>
                </div>
              </div>

              <!-- ── Rutinas — multi-select con checkboxes ── -->
              <div class="col-12">
                <label class="form-label label-divisan">
                  Rutinas <span class="text-danger">*</span>
                  <span class="ms-2 text-muted fw-normal" style="font-size: 0.78rem">
                    (selecciona una o más)
                  </span>
                </label>

                <!-- Buscador de rutinas -->
                <div class="search-rutina mb-2">
                  <i class="bi bi-search"></i>
                  <input
                    v-model="busquedaRutina"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="Buscar rutina..."
                    style="padding-left: 2rem"
                  />
                </div>

                <div class="rutinas-lista">
                  <div
                    v-for="r in rutinasFiltradas"
                    :key="r.id"
                    class="rutina-check-row"
                    :class="{ seleccionada: form.rutinas.includes(r.id) }"
                    @click="toggleRutina(r.id)"
                  >
                    <div class="rutina-check-box">
                      <i
                        :class="
                          form.rutinas.includes(r.id)
                            ? 'bi bi-check-square-fill'
                            : 'bi bi-square'
                        "
                      ></i>
                    </div>
                    <div class="rutina-check-info">
                      <span class="fw-bold">{{ r.nombre }}</span>
                      <small class="d-block text-muted" style="font-size: 0.73rem">
                        {{ labelPeriodicidad(r.periodicidad) }}
                        <span v-if="r.area_nombre"> · {{ r.area_nombre }}</span>
                        · {{ r.rutina_actividades?.length || 0 }} actividades
                      </small>
                    </div>
                  </div>
                  <div
                    v-if="!rutinasFiltradas.length"
                    class="custom-dropdown-item text-muted"
                  >
                    Sin rutinas disponibles
                  </div>
                </div>

                <!-- Seleccionadas -->
                <div v-if="form.rutinas.length" class="mt-2">
                  <small class="text-verde fw-bold">
                    <i class="bi bi-check-circle me-1"></i>
                    {{ form.rutinas.length }} rutina(s) seleccionada(s)
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 pt-0 d-flex justify-content-between">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              data-bs-dismiss="modal"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="btn btn-nuevo py-2 px-4"
              :disabled="store.guardando || !form.maquina || !form.rutinas.length"
            >
              <span
                v-if="store.guardando"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Programar
              {{
                form.rutinas.length > 1 ? `(${form.rutinas.length} rutinas)` : "evento"
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useCalendarioStore, LABEL_PERIODICIDAD } from "@/store/calendarioStore";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";

const props = defineProps({
  fechaSeleccionada: { type: String, default: "" },
  maquinas: { type: Array, default: () => [] },
  rutinas: { type: Array, default: () => [] },
});

const emit = defineEmits(["creado"]);

const store = useCalendarioStore();
const modalEl = ref(null);
let modalInstance = null;

const form = ref({ maquina: "", rutinas: [] });
const dropMaquina = ref(false);
const busquedaRutina = ref("");

// ── Directiva click-outside ──────────────────────────────────
const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};

// ── Computed ─────────────────────────────────────────────────
const fechaFormateada = computed(() => {
  if (!props.fechaSeleccionada) return "";
  return new Date(props.fechaSeleccionada + "T12:00:00").toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
});

const rutinasFiltradas = computed(() => {
  const q = busquedaRutina.value.toLowerCase();
  if (!q) return props.rutinas;
  return props.rutinas.filter(
    (r) =>
      r.nombre.toLowerCase().includes(q) ||
      (r.area_nombre || "").toLowerCase().includes(q)
  );
});

// ── Helpers ──────────────────────────────────────────────────
const labelPeriodicidad = (v) => LABEL_PERIODICIDAD[v] || v || "";

function cerrarDropMaquina() {
  dropMaquina.value = false;
}

function elegirMaquina(id) {
  form.value.maquina = id;
  dropMaquina.value = false;
}

function toggleRutina(id) {
  const idx = form.value.rutinas.indexOf(id);
  if (idx === -1) form.value.rutinas.push(id);
  else form.value.rutinas.splice(idx, 1);
}

// ── Métodos ──────────────────────────────────────────────────
function abrir() {
  form.value = { maquina: "", rutinas: [] };
  dropMaquina.value = false;
  busquedaRutina.value = "";
  modalInstance?.show();
}

function cerrar() {
  modalInstance?.hide();
}

async function guardar() {
  if (!form.value.maquina || !form.value.rutinas.length) return;
  try {
    const resultado = await store.crearEventosBulk(
      form.value.maquina,
      form.value.rutinas,
      props.fechaSeleccionada
    );
    cerrar();
    emit("creado");

    const creados = resultado.creados?.length || 0;
    const omitidos = resultado.omitidos?.length || 0;
    const errores = resultado.errores?.length || 0;

    if (errores) {
      Swal.fire({
        icon: "warning",
        title: "Programación con advertencias",
        html: `${creados} evento(s) creado(s).<br>${omitidos} ya existían (omitidos).<br>${errores} con error.`,
        confirmButtonColor: "var(--d-brown)",
      });
    } else {
      Swal.fire({
        icon: "success",
        title: `${creados} evento(s) programado(s)`,
        text: omitidos
          ? `${omitidos} rutina(s) ya estaban programadas y se omitieron.`
          : fechaFormateada.value,
        timer: 2200,
        showConfirmButton: false,
      });
    }
  } catch (err) {
    const msg = err?.response?.data
      ? Object.values(err.response.data).flat().join(" ")
      : "No se pudo programar el evento.";
    Swal.fire({
      icon: "error",
      title: "Error",
      text: msg,
      confirmButtonColor: "var(--d-brown)",
    });
  }
}

onMounted(() => {
  modalInstance = new Modal(modalEl.value);
});
defineExpose({ abrir, cerrar });
</script>

<style scoped>
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}
.fecha-texto {
  color: var(--d-green);
  font-weight: 700;
  font-size: 0.85rem;
}
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  border-radius: 8px;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-nuevo:disabled {
  opacity: 0.6;
}

/* ── Custom dropdown Máquina ── */
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
  cursor: pointer;
  user-select: none;
  min-height: 38px;
  display: flex;
  align-items: center;
  color: var(--d-brown);
}
.placeholder-text {
  color: #6c757d;
}
.custom-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
  cursor: pointer;
  pointer-events: auto;
}
.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 1080;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(40, 54, 24, 0.15);
  max-height: 185px;
  overflow-y: auto;
}
.custom-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
  color: var(--d-brown);
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}

/* ── Multi-select Rutinas ── */
.search-rutina {
  position: relative;
}
.search-rutina i {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
}

.rutinas-lista {
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  max-height: 220px;
  overflow-y: auto;
  background: white;
}

.rutina-check-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.rutina-check-row:last-child {
  border-bottom: none;
}
.rutina-check-row:hover {
  background: rgba(96, 108, 56, 0.06);
}
.rutina-check-row.seleccionada {
  background: rgba(96, 108, 56, 0.1);
}

.rutina-check-box {
  font-size: 1.1rem;
  color: var(--d-green);
  flex-shrink: 0;
}
.rutina-check-row.seleccionada .rutina-check-box {
  color: var(--d-green-dark);
}

.rutina-check-info {
  flex-grow: 1;
}
.rutina-check-info .fw-bold {
  font-size: 0.88rem;
  color: var(--d-brown);
}
</style>
